import './TextField.scss';

import cn from 'classnames';
import React, { cloneElement, forwardRef, useMemo, useState } from 'react';

import { ReactComponent as EyeIcon } from '@/common/assets/icons/eye.svg';
import { ReactComponent as EyeCrossedIcon } from '@/common/assets/icons/eye-crossed.svg';
import Button from '@/common/components/atoms/Button/Button';
import TextInput from '@/common/components/atoms/TextInput/TextInput';

import FieldErrors from '../FieldErrors/FieldErrors';
import {
  TextFieldDefaultProps,
  TextFieldPropTypes,
} from './TextField.proptypes';

const TextField = forwardRef(
  (
    {
      autoComplete,
      autoFocus,
      children,
      className,
      context,
      disabled,
      errors,
      errorsIcon,
      icon,
      iconPosition,
      id,
      max,
      maxLength,
      min,
      minLength,
      name,
      onBlur,
      onChange,
      onClick,
      onFocus,
      onKeyDown,
      placeholder,
      position,
      readOnly,
      required,
      spellCheck,
      type,
      value,
    },
    ref,
  ) => {
    const [isRevealed, setIsRevealed] = useState(false);

    const handleToggleReveal = () => {
      setIsRevealed(!isRevealed);
    };

    const Icon = useMemo(() => {
      if (icon && type !== 'password') {
        return cloneElement(icon, { className: 'text-field__icon' });
      }

      return null;
    }, [icon, type]);

    return (
      <div
        className={cn(
          'text-field',
          `text-field--${position}`,
          `text-field--icon-${iconPosition}`,
          className,
          {
            'text-field--disabled': disabled,
            'text-field--readonly': readOnly,
            'text-field--error': errors && errors.filter((val) => val).length,
            'text-field--icon': icon,
            'text-field--required': required,
          },
        )}
        id={id}
        data-qa-id={context ? `${context}-text-field` : 'text-field'}
      >
        {children && (
          <label className="text-field__label" htmlFor={`${id}-text-input`}>
            {children}
          </label>
        )}
        <div className="text-field__input">
          {Icon}
          <TextInput
            ref={ref}
            autoComplete={autoComplete}
            autoFocus={autoFocus}
            context={context}
            disabled={disabled}
            id={`${id}-text-input`}
            max={max}
            maxLength={maxLength}
            min={min}
            minLength={minLength}
            name={name}
            onBlur={onBlur}
            onChange={onChange}
            onClick={onClick}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            readOnly={readOnly}
            required={required}
            revealed={isRevealed}
            spellCheck={spellCheck}
            type={type}
            value={value}
          />
          {type === 'password' && (
            <Button className="text-field__reveal" onClick={handleToggleReveal}>
              {isRevealed ? <EyeCrossedIcon /> : <EyeIcon />}
            </Button>
          )}
        </div>
        {errors && (
          <FieldErrors
            className="text-field__errors"
            errors={errors}
            icon={errorsIcon}
            name={name}
          />
        )}
      </div>
    );
  },
);

TextField.defaultProps = {
  ...TextFieldDefaultProps,
};

TextField.propTypes = {
  ...TextFieldPropTypes,
};

export default TextField;
